import { useCallback, useEffect, useMemo, useState } from "react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { Switch } from "@/components/ui/switch";
import useAuth from "@/hooks/useAuth";
import { toast } from "react-hot-toast";
import {
  getReceiptSettingsApi,
  updateReceiptSettingsApi,
} from "@/api/settings/receipt-settings";
import { restaurant_id as fallbackRestaurantId } from "@/api/lib/url";
import hasPermission from "@/hooks/hasPermission";

const CATEGORY_TABS = [
  { key: "pick_up", label: "Pick-up", permission: "back_office.settings.pick_up_receipt_settings" },
  { key: "in_house", label: "In-House", permission: "back_office.settings.in_house_receipt_settings" },
  // { key: "online", label: "Collection (Online)", permission: "back_office.settings.collection_receipt_settings" },
  { key: "delivery", label: "Delivery", permission: "back_office.settings.delivery_receipt_settings" },
  { key: "table", label: "Table", permission: "back_office.settings.table_receipt_settings" },
];

const COPY_TABS = [
  { key: "customer", label: "Customer Copy" },
  { key: "kitchen", label: "Kitchen Copy" },
];

const STATIC_KEY_TO_RECEIPT_NAME = {
  moto: "Moto",
  address: "Address",
  phone_number: "Phone Number",
  website: "Website",
  vat_id: "Vat ID",
  bid: "BID",
  outlet_name: "Outlet Name",
  welcome_message: "Welcome Message",
};

const RECEIPT_WIDTH = 48;

const receiptTypeTitle = {
  pick_up: "PICKUP",
  in_house: "IN-HOUSE",
  online: "Collection (Online)",
  delivery: "Delivery",
  table: "Table",
};

const previewFieldValues = {
  "Customer Name": "NICK BYLE",
  "Guest Number": "10",
  "Till ID": "23",
  "Customer Phone Number": "016589784512",
  Code: "A4P-992",
  TIPS: "10.00",
  "Order Type": "Take Away",
  Date: "19/07/2025",
  "Accepted Date": "15:30",
  "Loyalty": "5.00",
  "Service Charge": "10.00",
  Vat: "20%",
  "Operator Name": "Rasel Mahmud",
  "Order ID": "102",
  "Dispatch Date": "16:16",
  "Discount": "5.00",
  "Total Savings": "10.00",
  Note: "I may Delay.",
  "Day order Number": "002",
};

const previewItems = {
  customer: [
    { qty: "12X", desc: "Papadum", amount: "6.00" },
    { qty: "1X", desc: "Set Meal for Two", amount: "22.96" },
    { qty: "1X", desc: "Chicken Biryani", amount: "12.00" },
    { qty: "1X", desc: "Plain Naan", amount: "5.00" },
    { qty: "30X", desc: "Bombay Aloo", amount: "10.00" },
  ],
  kitchen: [
    { qty: "12X", desc: "Papadum" },
    { qty: "", desc: "modifier: less hot, less Salt" },
    { qty: "1X", desc: "Set Meal for Two" },
    { qty: "", desc: "Addons: extra beef, less lemon" },
    { qty: "1X", desc: "Chicken Biryani" },
    { qty: "1X", desc: "Plain Naan" },
    { qty: "30X", desc: "Bombay Aloo" },
    { qty: "", desc: "Promotion" },
  ],
};

const getStaticFieldDefaults = (restaurant = {}) => ({
  restaurant_name: {
    label: "Restaurant Name",
    enabled: true,
    lockToggle: true,
    value: restaurant?.name || "",
    placeholder: "Restaurant name",
  },
  moto: {
    label: "Motto",
    enabled: true,
    value: restaurant?.business?.description || "",
    placeholder: "Enter motto",
  },
  address: {
    label: "Address",
    enabled: true,
    value: restaurant?.address || "",
    placeholder: "Enter address",
  },
  phone_number: {
    label: "Phone Number",
    enabled: true,
    value: restaurant?.phone || restaurant?.mobile || "",
    placeholder: "Enter phone number",
  },
  website: {
    label: "Website",
    enabled: true,
    value: restaurant?.website
      ? String(restaurant.website).replace(/^https?:\/\//, "")
      : "",
    placeholder: "Enter website link",
  },
  vat_id: {
    label: "Vat ID",
    enabled: true,
    value: "65326353265",
    placeholder: "Enter vat id",
  },
  bid: {
    label: "BID",
    enabled: true,
    value: "Dine-in",
    placeholder: "Enter BID",
  },
  outlet_name: {
    label: "Outlet Name",
    enabled: true,
    value: "B2",
    placeholder: "Enter outlet name",
  },
  welcome_message: {
    label: "Welcome Message",
    enabled: true,
    value: "Thank you for your custom",
    placeholder: "Thank you for your custom",
  },
});

const mergeStaticFields = (defaults, saved = {}) => {
  const next = {};
  Object.keys(defaults).forEach((key) => {
    const defaultField = defaults[key];
    const savedField = saved[key] || {};
    next[key] = {
      ...defaultField,
      enabled:
        defaultField.lockToggle === true
          ? true
          : savedField.enabled ?? defaultField.enabled,
      value: savedField.value ?? defaultField.value,
    };
  });
  return next;
};

const getContextKey = (category, copyType) => `${category}__${copyType}`;

const buildStaticFieldsByContext = (restaurant = {}) => {
  const next = {};
  CATEGORY_TABS.forEach(({ key: categoryKey }) => {
    COPY_TABS.forEach(({ key: copyKey }) => {
      next[getContextKey(categoryKey, copyKey)] = getStaticFieldDefaults(restaurant);
    });
  });
  return next;
};

const mergeStaticFieldsByContext = (defaultsByContext, savedByContext = {}) => {
  const next = {};
  Object.keys(defaultsByContext).forEach((contextKey) => {
    next[contextKey] = mergeStaticFields(
      defaultsByContext[contextKey],
      savedByContext[contextKey] || {},
    );
  });
  return next;
};

const cloneDeep = (value) => JSON.parse(JSON.stringify(value || {}));

const fit = (text = "", width = RECEIPT_WIDTH) => {
  const value = String(text);
  if (value.length <= width) return value;
  return `${value.slice(0, Math.max(0, width - 3))}...`;
};

const line = (char = "-") => char.repeat(RECEIPT_WIDTH);

const centered = (text = "") => {
  const value = fit(text, RECEIPT_WIDTH);
  const left = Math.max(0, Math.floor((RECEIPT_WIDTH - value.length) / 2));
  const right = Math.max(0, RECEIPT_WIDTH - value.length - left);
  return `${" ".repeat(left)}${value}${" ".repeat(right)}`;
};

const rightAligned = (text = "") => {
  const value = fit(text, RECEIPT_WIDTH);
  return `${" ".repeat(Math.max(0, RECEIPT_WIDTH - value.length))}${value}`;
};

const leftRight = (leftText = "", rightText = "") => {
  const left = String(leftText);
  const right = String(rightText);

  if (!left && !right) return "";
  if (!left) return rightAligned(right);
  if (!right) return fit(left, RECEIPT_WIDTH);

  const gap = 2;
  const maxLeft = Math.max(0, RECEIPT_WIDTH - right.length - gap);
  const safeLeft = fit(left, maxLeft);
  const spaces = Math.max(1, RECEIPT_WIDTH - safeLeft.length - right.length);

  return `${safeLeft}${" ".repeat(spaces)}${right}`;
};

const itemRow = (item, isKitchenCopy) => {
  if (isKitchenCopy) {
    const qty = (item.qty || "").padEnd(4, " ");
    return fit(`${qty}${item.desc || ""}`, RECEIPT_WIDTH);
  }

  const qty = (item.qty || "").padEnd(4, " ");
  const amount = String(item.amount || "");
  const descWidth = Math.max(0, RECEIPT_WIDTH - qty.length - amount.length);
  const desc = fit(item.desc || "", descWidth).padEnd(descWidth, " ");
  return `${qty}${desc}${amount}`;
};

const RECEIPT_ITEM_VISUAL_ORDER = {
  customer: [
    "Separator 1",
    "BID",
    "Outlet Name",
    "Till ID",
    "Operator Name",
    "Order ID",
    "Date",
    "Separator 2",
    "Order Type",
    "Code",
    "Guest Number",
    "Accepted Date",
    "Dispatch Date",
    "Separator 3",
    "Customer Name",
    "Customer Phone Number",
    "Separator 4",
    "Loyalty",
    "Service Charge",
    "Discount",
    "Total Savings",
    "Vat",
    "TIPS",
    "Separator 5",
    "Day order Number",
    "Separator 6",
    "Separator 7",
    "Note",
    "Separator 8",
  ],
  kitchen: [
    "Separator 1",
    "BID",
    "Outlet Name",
    "Till ID",
    "Operator Name",
    "Order ID",
    "Date",
    "Separator 2",
    "Order Type",
    "Code",
    "Guest Number",
    "Accepted Date",
    "Dispatch Date",
    "Separator 3",
    "Customer Name",
    "Customer Phone Number",
    "Separator 4",
    "Note",
    "Separator 5",
    "Day order Number",
    "Separator 6",
    "Separator 7",
    "Separator 8",
    "Loyalty",
    "Service Charge",
    "Discount",
    "Total Savings",
    "Vat",
    "TIPS",
  ],
};

const getOrderedItemsForDisplay = (
  items = [],
  copyType = "customer",
  category = "pick_up",
) => {
  const hiddenBottomItems = new Set([
    "moto",
    "address",
    "phone number",
    "website",
    "vat id",
    "tel number",
    "customer copy",
    "welcome message",
    "Loyalty",
    "bid",
  ]);

  const mapByName = new Map(
    items.map((item) => [String(item?.name || "").toLowerCase().trim(), item]),
  );

  const ordered = [];
  const visualOrder = RECEIPT_ITEM_VISUAL_ORDER[copyType] || RECEIPT_ITEM_VISUAL_ORDER.customer;

  visualOrder.forEach((name) => {
    const found = mapByName.get(name.toLowerCase());
    if (found) {
      ordered.push(found);
      mapByName.delete(name.toLowerCase());
    }
  });

  mapByName.forEach((item) => ordered.push(item));

  return ordered.filter((item) => {
    const normalizedName = String(item?.name || "").toLowerCase().trim();
    if (hiddenBottomItems.has(normalizedName)) return false;
    if (category !== "table" && normalizedName === "code") return false;
    return true;
  });
};

const getReceiptItemLabel = (item, category = "pick_up") => {
  if (item?.type === "separator") {
    return `${item.name} {--------}`;
  }

  if (String(item?.name || "").toLowerCase().trim() === "Loyalty") {
    return "Loyalty";
  }

  if (String(item?.name || "").toLowerCase().trim() === "Discount") {
    return "Discount";
  }

  if (category === "table" && String(item?.name || "").toLowerCase().trim() === "code") {
    return "Table";
  }

  return item?.name || "";
};

const getSeparatorHelpText = (
  separatorName = "",
  copyType = "customer",
  category = "pick_up",
) => {
  const normalized = String(separatorName).toLowerCase().trim();

  const customerBaseMap = {
    "separator 1": "Header and BID/outlet block",
    "separator 2": "BID/meta and receipt type title",
    "separator 3": "Receipt type and customer/order summary",
    "separator 4": "Customer summary and item header",
    "separator 5": "Item header and item list",
    "separator 6": "Item list and totals block",
    "separator 7": "Totals and payment details",
    "separator 8": "UNPAID block and welcome message",
  };

  const kitchenBaseMap = {
    "separator 1": "Header and BID/outlet block",
    "separator 2": "BID/meta and receipt type title",
    "separator 3": "Receipt type and customer summary",
    "separator 4": "Customer summary and item header",
    "separator 5": "Item header and kitchen items",
    "separator 6": "Kitchen items and note section",
    "separator 7": "Note section and UNPAID block",
    "separator 8": "UNPAID block and payment details",
  };

  const categoryOverrides = {
    table: {
      customer: {
        "separator 3": "Table/guest row and customer/order summary",
      },
      kitchen: {
        "separator 3": "Table/guest row and customer summary",
      },
    },
    delivery: {
      customer: {
        "separator 8": "Delivery address block and welcome message",
      },
      kitchen: {
        "separator 8": "Delivery address block and payment details",
      },
    },
    online: {
      customer: {
        "separator 3": "Collection title and customer/order summary",
      },
      kitchen: {
        "separator 3": "Collection title and customer summary",
      },
    },
    in_house: {
      customer: {
        "separator 3": "In-house title and customer/order summary",
      },
      kitchen: {
        "separator 3": "In-house title and customer summary",
      },
    },
    pick_up: {
      customer: {
        "separator 3": "Pickup title and customer/order summary",
      },
      kitchen: {
        "separator 3": "Pickup title and customer summary",
      },
    },
  };

  const base = copyType === "kitchen" ? kitchenBaseMap : customerBaseMap;
  const overrides = categoryOverrides?.[category]?.[copyType] || {};
  return overrides[normalized] || base[normalized] || "Preview divider";
};

const buildReceiptPreviewText = ({
  restaurant,
  staticFields,
  category,
  supCategory,
  activeItems,
}) => {
  const hasSeparatorConfig = (activeItems || []).some(
    (item) => item?.type === "separator",
  );

  const isSeparatorOn = (index) => {
    if (!hasSeparatorConfig) return true;

    const byReceiptItemId = (activeItems || []).find(
      (item) => item?.type === "separator" && Number(item?.receipt_item_id) === 18 + index,
    );

    if (byReceiptItemId) {
      return Boolean(byReceiptItemId.is_active);
    }

    const byName = (activeItems || []).find((item) => {
      if (item?.type !== "separator") return false;
      const normalizedName = String(item?.name || "").toLowerCase().trim();
      return normalizedName === `separator ${index}`;
    });

    return Boolean(byName?.is_active);
  };

  const pushSeparator = (receiptLines, index) => {
    if (isSeparatorOn(index)) {
      receiptLines.push(line("-"));
    }
  };

  const enabledFieldSet = new Set(
    (activeItems || [])
      .filter((item) => Boolean(item.is_active))
      .map((item) => String(item.name || "").toLowerCase()),
  );

  const hasField = (name) => enabledFieldSet.has(String(name).toLowerCase());
  const getValue = (name) => previewFieldValues[name] || "";
  const isStaticOn = (key) => Boolean(staticFields?.[key]?.enabled);
  const getStaticValue = (key, fallback = "") => {
    const value = staticFields?.[key]?.value;
    if (value === undefined || value === null || value === "") {
      return fallback;
    }
    return String(value);
  };
  const isKitchenCopy = supCategory === "kitchen";

  const restaurantName = getStaticValue(
    "restaurant_name",
    restaurant?.trading_name || restaurant?.name || "NAGA INDIAN",
  ).toUpperCase();
  const website = getStaticValue("website", "www.demo.com");
  const address = getStaticValue(
    "address",
    restaurant?.address || "68 Brick Ln, London E1 6RF, United Kingdom",
  );

  const receiptLines = [];
  receiptLines.push(centered(restaurantName));
  if (isStaticOn("moto")) {
    receiptLines.push(centered(getStaticValue("moto", "Fresh and tasty meals")));
  }
  if (isStaticOn("address")) {
    receiptLines.push(centered(address));
  }
  if (isStaticOn("phone_number")) {
    receiptLines.push(centered(`Phone: ${getStaticValue("phone_number", "011-2233676")}`));
  }
  if (isStaticOn("website")) {
    receiptLines.push(centered(`Website: ${website}`));
  }
  if (isStaticOn("vat_id")) {
    receiptLines.push(centered(`Vat ID:${getStaticValue("vat_id", "65326353265")}`));
  }
  pushSeparator(receiptLines, 1);

  receiptLines.push(
    leftRight(
      isStaticOn("bid") ? `BID:${getStaticValue("bid", "Dine-in")}` : "",
      isStaticOn("outlet_name")
        ? `Outlet:${getStaticValue("outlet_name", "B2")}`
        : "",
    ),
  );
  receiptLines.push(
    leftRight(
      hasField("Operator Name") ? `Operator:${getValue("Operator Name")}` : "",
      hasField("Date") ? getValue("Date") : "",
    ),
  );
  receiptLines.push(
    leftRight(
      hasField("Till ID") ? `Till ID:${getValue("Till ID")}` : "",
      hasField("Order ID") ? `Order ID:${getValue("Order ID")}` : "",
    ),
  );
  pushSeparator(receiptLines, 2);
  receiptLines.push(centered(receiptTypeTitle[category] || "PICKUP"));
  pushSeparator(receiptLines, 3);

  if (category === "table") {
    receiptLines.push(
      leftRight(
        hasField("Code") ? "Table: 27" : "",
        hasField("Guest Number") ? `Guest:${getValue("Guest Number")}` : "",
      ),
    );
  }

  receiptLines.push(
    leftRight(
      hasField("Accepted Date") ? `Accepted : ${getValue("Accepted Date")}` : "",
      hasField("Dispatch Date") ? `Dispatch: ${getValue("Dispatch Date")}` : "",
    ),
  );
  receiptLines.push(rightAligned(isKitchenCopy ? "Kitchen Copy" : "Customer Copy"));
  receiptLines.push("");

  if (hasField("Customer Name")) {
    receiptLines.push(`Customer : ${getValue("Customer Name")}`);
  }
  if (hasField("Customer Phone Number")) {
    receiptLines.push(`Tel: ${getValue("Customer Phone Number")}`);
  }
  pushSeparator(receiptLines, 4);

  if (isKitchenCopy) {
    receiptLines.push(fit("Qty Description", RECEIPT_WIDTH));
  } else {
    receiptLines.push(itemRow({ qty: "Qty", desc: "Description", amount: "Amount" }, false));
  }
  pushSeparator(receiptLines, 5);

  const items = previewItems[isKitchenCopy ? "kitchen" : "customer"];
  items.forEach((item) => {
    receiptLines.push(itemRow(item, isKitchenCopy));
  });

  if (!isKitchenCopy) {
    receiptLines.push("");
    pushSeparator(receiptLines, 6);
    receiptLines.push(leftRight("Items:5", "Order Total      221.00"));
    if (hasField("Loyalty")) receiptLines.push(rightAligned(`Loyalty          ${getValue("Loyalty")}`));
    if (hasField("Discount")) receiptLines.push(rightAligned(`Discount         ${getValue("Discount")}`));
    if (hasField("Service Charge")) receiptLines.push(rightAligned(`Service Charge    ${getValue("Service Charge")}`));
    if (hasField("Total Savings")) receiptLines.push(rightAligned(`Total Savings     ${getValue("Total Savings")}`));
    if (hasField("Vat")) receiptLines.push(rightAligned(`vat ${getValue("Vat")}           10.00`));
    receiptLines.push(rightAligned("Bill Amount 198.00"));
    if (hasField("TIPS")) receiptLines.push(rightAligned(`TIPS              ${getValue("TIPS")}`));
    pushSeparator(receiptLines, 7);
    receiptLines.push(rightAligned("TOTAL TO PAY      207.00"));
    receiptLines.push(rightAligned("Card payment      150.00"));
    receiptLines.push(rightAligned("Cash Payment       50.00"));
    receiptLines.push(rightAligned("Change              1.00"));
  } else {
    pushSeparator(receiptLines, 6);
  }

  receiptLines.push("");
  if (hasField("Note")) {
    receiptLines.push(`NOTES: ${getValue("Note")}`);
  }
  receiptLines.push("");
  receiptLines.push(
    leftRight(
      "UNPAID",
      hasField("Day order Number") ? getValue("Day order Number") : "",
    ),
  );

  if (category === "delivery") {
    receiptLines.push("");
    receiptLines.push("Delivery To");
    receiptLines.push("Rasel Mahmud");
    receiptLines.push("52 St Crispin Road");
    receiptLines.push("Norwich");
    receiptLines.push("NR3 5EF");
    receiptLines.push("Tel: 0121574953265526");
  }

  receiptLines.push("");
  pushSeparator(receiptLines, 8);
  if (!isKitchenCopy && isStaticOn("welcome_message")) {
    receiptLines.push(centered(getStaticValue("welcome_message", "Thank you for your custom")));
  }
  receiptLines.push("");
  receiptLines.push("CASH PAID");
  receiptLines.push("PAID BY CARD");

  return receiptLines
    .map((receiptLineText) => fit(receiptLineText, RECEIPT_WIDTH))
    .join("\n");
};

function ReceiptManagement() {  
  const { user } = useAuth();
  
  const filteredCategoryTabs = useMemo(() => {
    return CATEGORY_TABS.filter(
      (tab) => !tab.permission || hasPermission(tab.permission)
    );
  }, []);

  const [category, setCategory] = useState(() => {
    const allowed = CATEGORY_TABS.filter(
      (tab) => !tab.permission || hasPermission(tab.permission)
    );
    return allowed[0]?.key || "";
  });
  const [supCategory, setSubCategory] = useState("customer");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restaurant, setRestaurant] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const [initialStaticFieldsByContext, setInitialStaticFieldsByContext] = useState({});
  const [staticFieldsByContext, setStaticFieldsByContext] = useState({});
  const [receiptSettings, setReceiptSettings] = useState({});
  const [changedItems, setChangedItems] = useState({});

  const activeItems = useMemo(
    () => receiptSettings?.[category]?.[supCategory] || [],
    [receiptSettings, category, supCategory],
  );
  const displayItems = useMemo(
    () => getOrderedItemsForDisplay(activeItems, supCategory, category),
    [activeItems, supCategory, category],
  );

  const restaurantId =
    user?.restaurant_id || localStorage.getItem("restaurant_id") || fallbackRestaurantId;
  const currentContextKey = getContextKey(category, supCategory);
  const staticFields = useMemo(
    () => staticFieldsByContext?.[currentContextKey] || getStaticFieldDefaults(restaurant),
    [staticFieldsByContext, currentContextKey, restaurant],
  );

  const fetchSettings = useCallback(async () => {
    if (!restaurantId) return;

    setLoading(true);
    const res = await getReceiptSettingsApi(restaurantId);

    if (res?.status === "success" && res?.data?.receipt_settings) {
      const restaurantData = res.data.restaurant || {};
      setRestaurant(restaurantData);
      setInitialSettings(cloneDeep(res.data.receipt_settings));
      setReceiptSettings(cloneDeep(res.data.receipt_settings));

      const defaultStaticFieldsByContext = buildStaticFieldsByContext(restaurantData);

      const savedByContextRaw = localStorage.getItem("receipt_static_fields_by_context");
      let savedByContext = savedByContextRaw ? JSON.parse(savedByContextRaw) : {};

      // Backward compatibility for previous shared static field format.
      if (!savedByContextRaw) {
        const legacyRaw = localStorage.getItem("receipt_static_fields");
        if (legacyRaw) {
          const legacySaved = JSON.parse(legacyRaw);
          if (legacySaved?.restaurant_name || legacySaved?.welcome_message) {
            savedByContext = {};
            Object.keys(defaultStaticFieldsByContext).forEach((contextKey) => {
              savedByContext[contextKey] = legacySaved;
            });
          }
        }
      }

      const mergedStaticFieldsByContext = mergeStaticFieldsByContext(
        defaultStaticFieldsByContext,
        savedByContext,
      );

      const apiWelcomeMessages = res?.data?.welcome_messages || {};
      CATEGORY_TABS.forEach(({ key: categoryKey }) => {
        const apiMessage = apiWelcomeMessages?.[categoryKey];
        if (typeof apiMessage !== "string") return;

        COPY_TABS.forEach(({ key: copyKey }) => {
          const contextKey = getContextKey(categoryKey, copyKey);
          const currentField = mergedStaticFieldsByContext?.[contextKey]?.welcome_message;

          mergedStaticFieldsByContext[contextKey] = {
            ...mergedStaticFieldsByContext[contextKey],
            welcome_message: {
              ...currentField,
              enabled: currentField?.enabled ?? true,
              value: apiMessage,
            },
          };
        });
      });

      // Keep static toggle states aligned with API receipt settings per context.
      CATEGORY_TABS.forEach(({ key: categoryKey }) => {
        COPY_TABS.forEach(({ key: copyKey }) => {
          const contextKey = getContextKey(categoryKey, copyKey);
          const contextItems = res?.data?.receipt_settings?.[categoryKey]?.[copyKey] || [];
          const currentFields = mergedStaticFieldsByContext?.[contextKey] || {};

          Object.entries(STATIC_KEY_TO_RECEIPT_NAME).forEach(([staticKey, receiptName]) => {
            const receiptItem = contextItems.find(
              (item) => String(item?.name || "").trim().toLowerCase() === receiptName.toLowerCase(),
            );

            if (!receiptItem || !currentFields?.[staticKey]) return;

            mergedStaticFieldsByContext[contextKey] = {
              ...mergedStaticFieldsByContext[contextKey],
              [staticKey]: {
                ...mergedStaticFieldsByContext[contextKey][staticKey],
                enabled: Boolean(receiptItem.is_active),
              },
            };
          });
        });
      });

      setInitialStaticFieldsByContext(cloneDeep(mergedStaticFieldsByContext));
      setStaticFieldsByContext(cloneDeep(mergedStaticFieldsByContext));
    } else {
      toast.error(res?.message || "Failed to load receipt settings.");
    }

    setLoading(false);
  }, [restaurantId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = (id, checked) => {
    console.log("Toggle", { id, checked });
    const newIsActive = Boolean(checked);
    const idKey = String(id);
    const currentItem = activeItems.find((item) => String(item.id) === idKey);
    if (!currentItem) return;
    if (String(currentItem?.managed_by || "").trim().toLowerCase() !== "client") return;

    setReceiptSettings((prev) => {
      const next = cloneDeep(prev);
      if (!next[category]) {
        next[category] = {};
      }
      next[category][supCategory] = (next[category]?.[supCategory] || []).map((item) => {
        if (String(item.id) !== idKey) return item;
        return { ...item, is_active: newIsActive };
      });
      return next;
    });

    const initialItem = initialSettings?.[category]?.[supCategory]?.find(
      (item) => String(item.id) === idKey,
    );
    const baselineIsActive = initialItem
      ? Boolean(initialItem.is_active)
      : Boolean(currentItem.is_active);

    setChangedItems((prev) => {
      const next = { ...prev };
      const nextContextChanges = { ...(next[currentContextKey] || {}) };
      const changedId = currentItem.id;
      const changedIdKey = String(changedId);

      if (baselineIsActive !== newIsActive) {
        nextContextChanges[changedIdKey] = { id: changedId, is_active: newIsActive };
      } else {
        delete nextContextChanges[changedIdKey];
      }

      if (Object.keys(nextContextChanges).length > 0) {
        next[currentContextKey] = nextContextChanges;
      } else {
        delete next[currentContextKey];
      }

      return next;
    });
  };

  const handleResetCurrent = () => {
    setReceiptSettings((prev) => {
      const next = cloneDeep(prev);
      if (!next[category]) {
        next[category] = {};
      }
      next[category][supCategory] = cloneDeep(
        initialSettings?.[category]?.[supCategory] || [],
      );
      return next;
    });

    setStaticFieldsByContext((prev) => ({
      ...prev,
      [currentContextKey]: cloneDeep(
        initialStaticFieldsByContext?.[currentContextKey] || getStaticFieldDefaults(restaurant),
      ),
    }));

    setChangedItems((prev) => {
      const next = { ...prev };
      delete next[currentContextKey];
      return next;
    });

    toast.success("Current copy reset to default.");
  };

  const isStaticFieldDisabled = useCallback(
    (staticKey) => {
      const receiptItemName = STATIC_KEY_TO_RECEIPT_NAME[staticKey];
      if (!receiptItemName) return false;
      const targetItem = activeItems.find(
        (item) =>
          String(item?.name || "").trim().toLowerCase() ===
          receiptItemName.toLowerCase(),
      );
      if (!targetItem) return false;
      return String(targetItem?.managed_by || "").toLowerCase() !== "client";
    },
    [activeItems],
  );

  const handleStaticToggle = (key, checked) => {
    if (isStaticFieldDisabled(key)) return;

    setStaticFieldsByContext((prev) => {
      const currentFields = prev?.[currentContextKey] || getStaticFieldDefaults(restaurant);
      return {
        ...prev,
        [currentContextKey]: {
          ...currentFields,
          [key]: {
            ...currentFields?.[key],
            enabled: checked,
          },
        },
      };
    });

    // Bridge static UI toggle to real receipt setting toggle from API.
    const receiptItemName = STATIC_KEY_TO_RECEIPT_NAME[key];
    if (!receiptItemName) return;

    const targetItem = activeItems.find(
      (item) =>
        String(item?.name || "").trim().toLowerCase() === receiptItemName.toLowerCase(),
    );

    if (targetItem) {
      handleToggle(targetItem.id, checked);
    }
  };

  const handleStaticValueChange = (key, value) => {
    setStaticFieldsByContext((prev) => {
      const currentFields = prev?.[currentContextKey] || getStaticFieldDefaults(restaurant);

      if (key === "welcome_message") {
        const categoryCustomerContext = getContextKey(category, "customer");
        const categoryKitchenContext = getContextKey(category, "kitchen");
        const customerFields =
          prev?.[categoryCustomerContext] || getStaticFieldDefaults(restaurant);
        const kitchenFields =
          prev?.[categoryKitchenContext] || getStaticFieldDefaults(restaurant);

        return {
          ...prev,
          [categoryCustomerContext]: {
            ...customerFields,
            welcome_message: {
              ...customerFields?.welcome_message,
              value,
            },
          },
          [categoryKitchenContext]: {
            ...kitchenFields,
            welcome_message: {
              ...kitchenFields?.welcome_message,
              value,
            },
          },
        };
      }

      return {
        ...prev,
        [currentContextKey]: {
          ...currentFields,
          [key]: {
            ...currentFields?.[key],
            value,
          },
        },
      };
    });
  };

  const handleSave = async () => {
    if (!restaurantId) {
      toast.error("Restaurant not found.");
      return;
    }

    const changedForCurrentContext = changedItems?.[currentContextKey] || {};
    const hasToggleChanges = Object.keys(changedForCurrentContext).length > 0;
    const hasStaticChanges =
      JSON.stringify(staticFieldsByContext) !==
      JSON.stringify(initialStaticFieldsByContext);

    if (!hasToggleChanges && !hasStaticChanges) {
      toast("No changes to save.");
      return;
    }

    setSaving(true);

    if (hasToggleChanges) {
      const payload = {
        receipt_settings: {
          [category]: {
            [supCategory]: Object.values(changedForCurrentContext),
          },
        },
      };

      const res = await updateReceiptSettingsApi(restaurantId, payload);

      if (res?.status === "success") {
        setInitialSettings(cloneDeep(receiptSettings));
        setChangedItems((prev) => {
          const next = { ...prev };
          delete next[currentContextKey];
          return next;
        });
      } else {
        toast.error(res?.message || "Failed to update receipt settings.");
        setSaving(false);
        return;
      }
    }

    if (hasStaticChanges) {
      localStorage.setItem(
        "receipt_static_fields_by_context",
        JSON.stringify(staticFieldsByContext),
      );
      setInitialStaticFieldsByContext(cloneDeep(staticFieldsByContext));
    }

    toast.success("Receipt settings updated successfully.");
    setSaving(false);
  };

  const previewTitle =
    COPY_TABS.find((tab) => tab.key === supCategory)?.label || "Preview";
  const previewText = useMemo(
    () =>
      buildReceiptPreviewText({
        restaurant,
        staticFields,
        category,
        supCategory,
        activeItems,
      }),
    [restaurant, staticFields, category, supCategory, activeItems],
  );


  // console.log("changedItems", changedItems);


  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Receipt Management" }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Settings", path: "" },
        { name: "Receipt Management", path: "/backoffice/receipt-management" },
      ]}
    >
      <div className="container flex flex-col gap-4 mt-5 pb-6">
        <div className="flex flex-wrap gap-3">
          {filteredCategoryTabs.map((tab) => (
            <button
              key={tab.key}
              className={`px-3 py-1 rounded-md text-sm ${
                category === tab.key
                  ? "bg-color6 text-cblack"
                  : "bg-color2 text-color5"
              }`}
              onClick={() => setCategory(tab.key)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="w-full flex items-center gap-3 border-b border-color3">
          {COPY_TABS.map((tab, index) => (
            <div key={tab.key} className="flex items-center gap-3">
              <button
                className={`cursor-pointer pb-2 border-b-2 text-sm ${
                  supCategory === tab.key
                    ? "text-color6 border-color6"
                    : "text-color5 border-transparent"
                }`}
                onClick={() => setSubCategory(tab.key)}
                type="button"
              >
                {tab.label}
              </button>
              {index !== COPY_TABS.length - 1 && (
                <div className="block w-[1px] h-4 bg-color3" />
              )}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="py-8 text-sm text-color5">Loading receipt settings...</div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <div className="bg-color2 border border-color3 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium text-cwhite">{previewTitle}</h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-3 py-1 rounded text-sm bg-cgreen text-cwhite disabled:opacity-60"
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={handleResetCurrent}
                    className="px-3 py-1 rounded text-sm bg-cpurple text-cwhite"
                  >
                    Reset to Default
                  </button>
                </div>
              </div>

              <div className="space-y-3 mb-5">
                <div>
                  <label className="text-sm text-cwhite block mb-1">
                    Restaurant Name <span className="text-cred">*</span>
                  </label>
                  <input
                    className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                    value={staticFields?.restaurant_name?.value || ""}
                    onChange={(event) =>
                      handleStaticValueChange("restaurant_name", event.target.value)
                    }
                    placeholder="Restaurant name"
                  />
                </div>
                <div>
                  <label className="text-sm text-cwhite mb-1 flex items-center gap-2">
                    <Switch
                      checked={Boolean(staticFields?.moto?.enabled)}
                      disabled={isStaticFieldDisabled("moto")}
                      onCheckedChange={(checked) => handleStaticToggle("moto", checked)}
                    />
                    Motto
                  </label>
                  <input
                    className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                    value={staticFields?.moto?.value || ""}
                    onChange={(event) =>
                      handleStaticValueChange("moto", event.target.value)
                    }
                    placeholder="Enter motto"
                  />
                </div>
                <div>
                  <label className="text-sm text-cwhite mb-1 flex items-center gap-2">
                    <Switch
                      checked={Boolean(staticFields?.address?.enabled)}
                      disabled={isStaticFieldDisabled("address")}
                      onCheckedChange={(checked) => handleStaticToggle("address", checked)}
                    />
                    Address
                  </label>
                  <input
                    className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                    value={staticFields?.address?.value || ""}
                    onChange={(event) =>
                      handleStaticValueChange("address", event.target.value)
                    }
                    placeholder="Address"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-cwhite mb-1 flex items-center gap-2">
                      <Switch
                        checked={Boolean(staticFields?.phone_number?.enabled)}
                        disabled={isStaticFieldDisabled("phone_number")}
                        onCheckedChange={(checked) =>
                          handleStaticToggle("phone_number", checked)
                        }
                      />
                      Phone Number
                    </label>
                    <input
                      className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                      value={staticFields?.phone_number?.value || ""}
                      onChange={(event) =>
                        handleStaticValueChange("phone_number", event.target.value)
                      }
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-cwhite mb-1 flex items-center gap-2">
                      <Switch
                        checked={Boolean(staticFields?.website?.enabled)}
                        disabled={isStaticFieldDisabled("website")}
                        onCheckedChange={(checked) => handleStaticToggle("website", checked)}
                      />
                      Website
                    </label>
                    <input
                      className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                      value={staticFields?.website?.value || ""}
                      onChange={(event) =>
                        handleStaticValueChange("website", event.target.value)
                      }
                      placeholder="Website"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-cwhite mb-1 flex items-center gap-2">
                    <Switch
                      checked={Boolean(staticFields?.vat_id?.enabled)}
                      disabled={isStaticFieldDisabled("vat_id")}
                      onCheckedChange={(checked) => handleStaticToggle("vat_id", checked)}
                    />
                    Vat ID
                  </label>
                  <input
                    className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                    value={staticFields?.vat_id?.value || ""}
                    onChange={(event) => handleStaticValueChange("vat_id", event.target.value)}
                    placeholder="Enter vat id"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-cwhite mb-1 flex items-center gap-2">
                      <Switch
                        checked={Boolean(staticFields?.bid?.enabled)}
                        disabled={isStaticFieldDisabled("bid")}
                        onCheckedChange={(checked) => handleStaticToggle("bid", checked)}
                      />
                      BID
                    </label>
                    <input
                      className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                      value={staticFields?.bid?.value || ""}
                      onChange={(event) => handleStaticValueChange("bid", event.target.value)}
                      placeholder="Enter BID"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-cwhite mb-1 flex items-center gap-2">
                      <Switch
                        checked={Boolean(staticFields?.outlet_name?.enabled)}
                        disabled={isStaticFieldDisabled("outlet_name")}
                        onCheckedChange={(checked) =>
                          handleStaticToggle("outlet_name", checked)
                        }
                      />
                      Outlet Name
                    </label>
                    <input
                      className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                      value={staticFields?.outlet_name?.value || ""}
                      onChange={(event) =>
                        handleStaticValueChange("outlet_name", event.target.value)
                      }
                      placeholder="Enter outlet name"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-5">
                {displayItems.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-2 ${
                      item?.type === "separator" ? "md:col-span-2 lg:col-span-3" : ""
                    }`}
                  >
                    <Switch
                      id={`receipt-item-${item.id}`}
                      checked={Boolean(item.is_active)}
                      disabled={String(item?.managed_by || "").toLowerCase() !== "client"}
                      onCheckedChange={(checked) => handleToggle(item.id, checked)}
                    />
                    <div className="flex flex-col">
                      <label
                        htmlFor={`receipt-item-${item.id}`}
                        className="text-sm text-cwhite3 leading-tight"
                      >
                        {getReceiptItemLabel(item, category)}
                      </label>
                      {item?.type === "separator" && (
                        <span className="text-xs text-color8 leading-tight">
                          {getSeparatorHelpText(item?.name, supCategory, category)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {supCategory !== "kitchen" && (
                <div className="mt-5">
                  <label className="text-sm text-cwhite mb-1 flex items-center gap-2">
                    <Switch
                      checked={Boolean(staticFields?.welcome_message?.enabled)}
                      disabled={isStaticFieldDisabled("welcome_message")}
                      onCheckedChange={(checked) =>
                        handleStaticToggle("welcome_message", checked)
                      }
                    />
                    Welcome Message
                  </label>
                  <input
                    className="w-full bg-color3 border-none outline-none text-color4 text-sm px-2 py-2 rounded"
                    value={staticFields?.welcome_message?.value || ""}
                    onChange={(event) =>
                      handleStaticValueChange("welcome_message", event.target.value)
                    }
                    placeholder="Thank you for your custom"
                  />
                </div>
              )}
            </div>

            <div className="bg-color2 border border-color3 p-4">
              <h3 className="text-base font-medium mb-4 text-cwhite">Preview</h3>
              <div className="w-full bg-[#111827] border border-color3 p-4 min-h-[620px] flex items-center justify-center overflow-auto">
                <pre className="text-cwhite3 text-[11px] leading-[1.55] whitespace-pre-wrap font-mono">
{previewText}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout2>
  );
}

export default ReceiptManagement;
